import { axiosClient } from '@/libs/axios-client';
import {
  extractFileNameFromDisposition,
  saveBlobAsFile,
} from '@/utils/exportUtils';

const BASE = '/equipment-requests';

/**
 * Company settings: { enabled, services: [{ key, name, emails }],
 * groups: [{ key, name, serviceKey, mode: multi|single|yesNo, yesIsItem,
 * implicit? }], items: [{ name, groupKey, serviceKey ('' = the group's),
 * withDetail }], otherItemsServiceKey, saved }. Saved the same way (plus
 * `company`). A list saved before groups comes as one `implicit` group.
 */
export const getEquipmentSettings = async (company) => {
  const { data } = await axiosClient.get(`${BASE}/settings`, {
    params: { company },
  });
  return data;
};

export const updateEquipmentSettings = async (payload) => {
  const { data } = await axiosClient.put(`${BASE}/settings`, payload);
  return data;
};

/** The import sheet with the company's current list in it. */
export const downloadEquipmentTemplate = async (company, language) => {
  const response = await axiosClient.get(`${BASE}/settings/import-template`, {
    params: { company, language },
    responseType: 'blob',
  });
  saveBlobAsFile(
    response.data,
    response.headers['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extractFileNameFromDisposition(response.headers['content-disposition']) ||
      'sablon-oprema.xlsx'
  );
};

/**
 * Reads an import sheet against the form's services ([{ key, name }]);
 * writes nothing. Answers { groups, items, rows: [{ row, group, item, mode,
 * detail, service, errors: [code] }], errorCount }.
 */
export const previewEquipmentImport = async (company, file, services) => {
  const form = new FormData();
  form.append('company', company);
  form.append('services', JSON.stringify(services || []));
  form.append('file', file);
  const { data } = await axiosClient.post(`${BASE}/settings/import-preview`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

/**
 * For the employee profile: { enabled, groups: [{ key, name, mode, implicit,
 * service, items: [{ name, withDetail, service }] }], items: [{ name, group,
 * service }], otherItemsService, newHire, position, sectorName, requests },
 * a service being { name, emails } or null
 * (each request item carries the `service` and `sentTo` it went to, and its
 * `state`: requested | assigned | returned, with dates, notes and history),
 * and `assigned`: the items the employee holds now.
 */
export const getEmployeeEquipment = async (employeeId) => {
  const { data } = await axiosClient.get(`${BASE}/employee/${employeeId}`);
  return data;
};

export const submitEquipmentRequest = async (employeeId, payload) => {
  const { data } = await axiosClient.post(
    `${BASE}/employee/${employeeId}`,
    payload
  );
  return data;
};

/**
 * Moves one item of a request: { item: name, group: its group ('' for none),
 * action: 'assign'|'return'|'undo',
 * date?: 'YYYY-MM-DD', note? }. Answers { request, message }.
 */
export const updateEquipmentItemState = async (
  employeeId,
  requestId,
  payload
) => {
  const { data } = await axiosClient.patch(
    `${BASE}/employee/${employeeId}/requests/${requestId}/item`,
    payload
  );
  return data;
};

/**
 * The employee's own view (own profile): the catalog as above, `requests`
 * with each one's `approval` ({ status: pending|approved|rejected,
 * decidedByName, decidedAt, note } or null when none was needed) and
 * `assigned`. Refused (403) when the account cannot create requests.
 */
export const getOwnEquipment = async (employeeId) => {
  const { data } = await axiosClient.get(`${BASE}/me`, {
    params: { employee: employeeId },
  });
  return data;
};

/**
 * The employee asks for themselves: { items, extras, note }. Answers
 * { request, status: 'pending'|'approved', sendFailed, message }.
 */
export const submitOwnEquipmentRequest = async (employeeId, payload) => {
  const { data } = await axiosClient.post(`${BASE}/me`, {
    ...payload,
    employee: employeeId,
  });
  return data;
};

/**
 * Approver's decision on an employee's own request: { decision:
 * 'approve'|'reject', note } (note required when rejecting). Answers
 * { request, sendFailed, message }.
 */
export const decideEquipmentRequest = async (employeeId, requestId, payload) => {
  const { data } = await axiosClient.patch(
    `${BASE}/employee/${employeeId}/requests/${requestId}/decision`,
    payload
  );
  return data;
};

/** Sends what did not go out to the services after an approval. */
export const resendEquipmentRequest = async (employeeId, requestId) => {
  const { data } = await axiosClient.post(
    `${BASE}/employee/${employeeId}/requests/${requestId}/resend`
  );
  return data;
};

// Column -> its sort param, as the standard lists name them (TableHeaderSort).
export const COMPANY_EQUIPMENT_SORTERS = Object.freeze({
  employeeName: 'sortEmployeeName',
  sectorName: 'sortSectorName',
  name: 'sortName',
  service: 'sortService',
  state: 'sortState',
  since: 'sortSince',
});

// A URL value with a comma comes back as an array (deserializeValue).
const textParam = (value) =>
  (Array.isArray(value) ? value.join(',') : value) || undefined;

/**
 * The panel's params (filters, `sort*` = 'asc'|'desc', `offset` = page,
 * `limit` = page size) as the endpoint takes them: `sorter` = JSON
 * { column: 'asc'|'desc' }.
 */
const overviewRequestParams = (params = {}) => {
  const sorter = {};
  Object.entries(COMPANY_EQUIPMENT_SORTERS).forEach(([field, key]) => {
    if (params[key] === 'asc' || params[key] === 'desc')
      sorter[field] = params[key];
  });
  return {
    company: params.company,
    show: params.show || undefined,
    service: textParam(params.service),
    search: textParam(params.search),
    employee: textParam(params.employee),
    sector: textParam(params.sector),
    item: textParam(params.item),
    sorter: Object.keys(sorter).length ? JSON.stringify(sorter) : undefined,
    offset: params.offset ?? undefined,
    limit: params.limit ?? undefined,
  };
};

/**
 * Company profile "Oprema" tile, one page: { items, count, counts:
 * { assigned, requested, returned, leaving }, services, filters }. `count` =
 * rows matching the filters, `counts` = the whole company. `limit: 0` =
 * counters and services only.
 */
export const getCompanyEquipmentOverview = async (params) => {
  const { data } = await axiosClient.get(`${BASE}/company`, {
    params: overviewRequestParams(params),
  });
  return data;
};

/** Every matching row as Excel (same filters and sort, no paging), saved as a file. */
export const exportCompanyEquipmentOverview = async (params) => {
  const response = await axiosClient.get(`${BASE}/company/export`, {
    params: {
      ...overviewRequestParams(params),
      offset: undefined,
      limit: undefined,
    },
    responseType: 'blob',
  });
  const contentType =
    response.headers['content-type'] ||
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const fileName =
    extractFileNameFromDisposition(response.headers['content-disposition']) ||
    'oprema.xlsx';
  saveBlobAsFile(response.data, contentType, fileName);
};
